import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useParams} from 'react-router-dom/cjs/react-router-dom.min'
import {HTTP_STATUS_OK, SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {useAuth} from '../../authentication/AuthProvider'
import {getClientId} from '../../authentication/AuthUtils'
import {actionTableKeys} from '../../new-table/Constants'
import {permissionOptionKeys} from '../../role/RoleConstant'
import {useRoleContext} from '../../role/RoleProvider'
import {
    getStaticListEventKey,
    onStaticListTableChangeRemoveListener,
    staticListEvent,
    staticListEventTypes,
} from '../../static-list/broadcast/StaticListEvent'
import {
    usePageStaticList,
    useStaticListCRUD,
} from '../../static-list/query-keys/StaticListKeysManager'
import {getPageStaticList} from '../../static-list/StaticListEndPoint'
import {useImportNotificationContext} from '../../table-utils/importForm/ImportNotificationProvider'
import {
    filterDataTableEvent,
    filterEventTypes,
    getFilterEventKey,
    removeFilterChangeListener,
} from '../broadcast/FilterEvent'
import {exportColumnTypes, exportRecordTypes, exportTypes} from '../Constant'
import StyledCardListWrapper from '../StyledCardListWrapper'
import StyledDeleteDialog from '../StyledDeleteDialog'
import {
    getAdvancedFilter,
    getShouldRememberAdvancedFilter,
    tableTypes,
} from '../StyledTableUtils'

const StyledHealthCarePractitionerStaticListDatatable = () => {
    useSignals()

    const queryClient = useQueryClient()
    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        if (!idString) {
            return null
        }
        return parseInt(idString)
    }, [idString])
    //#endregion

    //#region Context
    const {user} = useAuth()
    const {translateCommons} = TranslateUtils()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {hcpManagementPermissions} = useRoleContext()
    const {handleOnExportDatatable} = useImportNotificationContext()
    //#endregion

    //#region Load Data from Local Storage
    const initialAdvancedFilterRequest = useMemo(() => {
        const didRememberSettings = getShouldRememberAdvancedFilter(
            tableTypes.hcpStaticList,
        )

        if (didRememberSettings) {
            const storedFilterRequest = getAdvancedFilter(
                tableTypes.hcpStaticList,
            )
            if (
                !!storedFilterRequest &&
                Object.keys(storedFilterRequest).length > 0
            ) {
                return storedFilterRequest
            }
        }

        return {}
    }, [])
    //#endregion

    const deleteRef = useRef(null)

    //#region State Controls
    const pageable = useSignal({
        pageRows: 10,
        pageNumber: 0,
        sortField: 'createdAt',
        sortOrder: -1,
    })

    const filterRequest = useSignal({
        globalSearchValue: null,
        ownerId: id,
        hospitalId:
            userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        ...initialAdvancedFilterRequest,
    })

    // Pageable
    const handleChangeSortColumn = useCallback(
        e => {
            pageable.value = {
                ...pageable.value,
                sortField: e.sortField,
                sortOrder: e.sortOrder === 1 ? -1 : 1,
            }
        },
        [pageable.value.sortField, pageable.value.sortOrder],
    )

    const handleChangePageNumber = useCallback(
        newPageNumber => {
            pageable.value = {
                ...pageable.value,
                pageNumber: newPageNumber,
            }

            handlePrefetchDataByPage(newPageNumber + 1)
        },
        [pageable.value.pageNumber],
    )

    const handleChangePageRows = useCallback(
        newPageRows => {
            pageable.value = {
                ...pageable.value,
                pageRows: newPageRows,
                pageNumber: 0,
            }

            pagePrefetchRef.current = [0]

            handlePrefetchDataByPage(1)
        },
        [pageable.value.pageRows],
    )

    // Global Search Value
    const handleChangeGlobalSearch = useCallback(value => {
        filterRequest.value = {
            ...filterRequest.value,
            globalSearchValue: value || null,
        }
    }, [])

    // Filter Request
    const handleClearFilterRequest = () => {
        filterRequest.value = {
            globalSearchValue: null,
            ownerId: id,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
        }
    }

    const handleClearFilterRequestByKey = useCallback((key, value) => {
        filterRequest.value = {
            ...filterRequest.value,
            [key]: value,
        }
    }, [])

    const handleClearAdvancedFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: filterRequest.value?.globalSearchValue || null,
            ownerId: id,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
        }
    }, [])

    // Table Action CRUD
    const formRef = useRef(null)

    const {mutateAsync: deleteStaticList} = useStaticListCRUD(
        QueryCRUDTypes.delete,
        user?.id,
    )

    // const handleAddNew = useCallback(() => {
    //     formRef.current?.showForm()
    // }, [])

    const handleOnDelete = async data => {
        if (!!data && !!data?.id) {
            const response = await deleteStaticList(data)
            if (response === HTTP_STATUS_OK) {
                const {staticListKeys} = QueryKeyManager(
                    QueryKeyTypes.staticList,
                    user?.id,
                )
                //TODO: Temporary Refetch Table
                queryClient.invalidateQueries(staticListKeys.pattern())
            }
        }
    }

    const handleAction = (type, rowValue) => {
        if (type === actionTableKeys.delete && rowValue) {
            handleOnDelete(rowValue)
        }
    }
    //#endregion

    //#region Check Permission
    const handleCheckPermissionByKey = useCallback(
        key => {
            if (!key) {
                return false
            }

            if (userRole === SystemRole.ADMIN) {
                return true
            }

            if (!hcpManagementPermissions?.length) {
                return false
            }

            if (key === permissionOptionKeys.allowToDelete && user?.id === id) {
                return hcpManagementPermissions?.includes(
                    permissionOptionKeys.allowToRemoveHcpStaticList,
                )
            } else {
                return hcpManagementPermissions?.includes(key)
            }
        },
        [hcpManagementPermissions, id, user?.id, userRole],
    )
    //#endregion

    //#region Data Controls
    const getCurrentQueryKey = ({newPageNumber, newPageRows}) => {
        const {staticListKeys} = QueryKeyManager(
            QueryKeyTypes.staticList,
            user?.id,
        )

        return staticListKeys.page({
            pageNumber:
                newPageNumber !== null && typeof newPageNumber === 'number'
                    ? newPageNumber
                    : pageable.value.pageNumber,
            pageRows:
                newPageRows !== null && typeof newPageNumber === 'number'
                    ? newPageRows
                    : pageable.value.pageRows,
            sortField: pageable.value.sortField,
            sortOrder: pageable.value.sortOrder,
            filterRequest: {
                ...filterRequest.value,
            },
        })
    }

    const {data: queryData, isFetched} = usePageStaticList(
        user?.id,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        filterRequest.value,
        true,
    )

    const dataList = useRef([])
    const currentDataList = useMemo(() => {
        dataList.current = queryData?.content || []

        return queryData?.content || []
    }, [queryData?.content])

    // Page Rows from Pagination
    const rowsOptionHoveredRef = useRef([10])
    const handlePrefetchDataByRowsOption = useCallback(rows => {
        if (!!user?.id && !rowsOptionHoveredRef.current?.includes(rows)) {
            rowsOptionHoveredRef.current.push(rows)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: 0,
                    newPageRows: rows,
                }),
                queryFn: () =>
                    getPageStaticList(
                        user?.id,
                        0,
                        rows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    const handleOnDetailRowClick = id => {
        if (!!id) {
            const selectedRowData = dataList.current?.find(row => row.id === id)
            if (!!selectedRowData) {
                formRef.current?.showForm(selectedRowData)
            }
        }
    }

    // Page Number from Pagination
    const pagePrefetchRef = useRef([0])
    const handlePrefetchDataByPage = useCallback(pageNumber => {
        if (!!user?.id && !pagePrefetchRef.current?.includes(pageNumber)) {
            pagePrefetchRef.current.push(pageNumber)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: pageNumber,
                    newPageRows: pageable.peek().pageRows,
                }),
                queryFn: () =>
                    getPageStaticList(
                        user?.id,
                        pageNumber,
                        pageable.peek().pageRows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    //#region Event Listener on State Changes
    useEffect(() => {
        // TABLE CHANGE
        staticListEvent.on(
            `${getStaticListEventKey(staticListEventTypes.TABLE_CHANGE)}`,
            data => {
                if (
                    !data ||
                    !data?.id ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (
                    userRole === SystemRole.USER_WITH_ROLE &&
                    (!data?.hospitalId ||
                        data?.hospitalId !== currentHospital?.id)
                ) {
                    return
                }

                if (!data?.ownerId || data?.ownerId !== id) {
                    return
                }

                const {staticListKeys} = QueryKeyManager(
                    QueryKeyTypes.staticList,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: staticListKeys.pattern(),
                })
            },
        )

        // PREFECT PAGE
        handlePrefetchDataByPage(pageable.value.pageNumber + 1)

        // FILTER
        filterDataTableEvent.on(
            `${getFilterEventKey(
                tableTypes.hcpStaticList,
                filterEventTypes.advanced,
            )}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearAdvancedFilterRequest()

                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        return () => {
            removeFilterChangeListener(
                tableTypes.hcpStaticList,
                filterEventTypes.advanced,
            )

            onStaticListTableChangeRemoveListener()
        }
    }, [])
    //#endregion

    //#region Export Data
    const handleRetrieveExportData = async (
        {
            exportType = exportTypes.excel, // csv, excel, pdf
            exportRecordType = exportRecordTypes.allRecords, // all, currentInView
            exportColumns = exportColumnTypes.allColumns, // all, currentInView
        },
        columns,
    ) => {
        handleOnExportDatatable(
            {
                exportType: exportType,
                exportRecordType: exportRecordType,
                exportColumns: exportColumns,
            },
            queryData,
            pageable.value,
            filterRequest.value,
            columns,
            tableTypes.hcpStaticList,
        )
    }
    //#endregion

    //#region template
    const template = item => {
        return (
            <>
                <div className="my-2 font-bold text-lg text-teal-600 min-h-[28px] flex items-center">
                    {item?.hospitalName || 'N/a'}
                </div>

                <div className="text-md text-gray-800 font-medium mx-2 mb-2 flex gap-2 min-h-[24px] items-center">
                    <i className="pi pi-slack mt-1" /> Name:
                </div>
                <div
                    className="text-md px-1 text-gray-600 font-medium mx-2 mb-2 
                   h-[100px] overflow-hidden line-clamp-3">
                    - {item?.name || 'No name'}
                </div>
                <div className="text-md text-gray-800 font-medium mx-2 mb-2 flex gap-2 min-h-[24px] items-center">
                    <i className="pi pi-users mt-1" /> Team size:
                    <span className="text-gray-600 min-w-[40px]">
                        {item?.numberOfParticipants || 'N/A'}
                    </span>
                </div>

                <div className="text-md text-gray-800 font-medium mx-2 mb-1 flex gap-2 min-h-[28px] items-center">
                    <i className="pi pi-inbox mt-1" /> Description:
                </div>

                <div
                    className="text-md px-1 text-gray-600 font-medium mx-2 mb-2 
                   h-[100px] overflow-hidden line-clamp-3">
                    - {item?.description || 'No description'}
                </div>
            </>
        )
    }
    //#endregion

    return (
        // <DatatableContext.Provider
        //     value={{
        //         dataKey: 'id',
        //         tableType: tableTypes.hcpStaticList,
        //         queryData: queryData,
        //         isFetched: isFetched,
        //         columnList: hcpStaticListColumnList,
        //         tableToolbar: [tableToolbarType.tableControlToolbar],
        //         tableActions: [actionTableKeys.delete],

        //         pageable: pageable.value,
        //         onChangeSortColumn: handleChangeSortColumn,
        //         onChangePageNumber: handleChangePageNumber,
        //         onChangePageRows: handleChangePageRows,
        //         onPageRowsHovered: handlePrefetchDataByRowsOption,

        //         onDetailRowClick: handleOnDetailRowClick,
        //         handleCheckPermissionByKey: handleCheckPermissionByKey,

        //         initialAdvancedFilterRequest: initialAdvancedFilterRequest,
        //         handleClearFilterRequest: handleClearFilterRequest,
        //         handleClearFilterRequestByKey: handleClearFilterRequestByKey,
        //         handleClearAdvancedFilterRequest:
        //             handleClearAdvancedFilterRequest,

        //         handleRetrieveExportData: handleRetrieveExportData,

        //         handleChangeGlobalSearch: handleChangeGlobalSearch,

        //         hideCreateButton: true,
        //         // handleAddNew: handleAddNew,
        //         handleAction: handleAction,
        //     }}>
        //     <StyledDataTableWrapper
        //         headerOptions={{
        //             title: 'Static List Management',
        //             subTitle:
        //                 'Manage the static lists created by this health care.',
        //         }}
        //     />
        // </DatatableContext.Provider>
        <>
            <StyledCardListWrapper
                isAdd={false}
                isEdit={false}
                handleAddNew={() => {}}
                handleAction={() => {}}
                handleOnDelete={handleOnDelete}
                initialData={queryData?.content || []}
                className={`min-h-screen b`}
                template={template}
                title={translateCommons(' Support Teams Management')}
                description={
                    'Manage the participating health care support teams.'
                }
            />
            <StyledDeleteDialog ref={deleteRef} onDelete={handleOnDelete} />
        </>
    )
}

export default StyledHealthCarePractitionerStaticListDatatable
